import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Marquee from 'react-fast-marquee';
import FlavorCard from '../components/FlavorCard';
import WaitlistForm from '../components/WaitlistForm';
import VoteSection from '../components/VoteSection';
import FaqSection from '../components/FaqSection';
import FloatingSticker from '../components/FloatingSticker';
import FounderNoteModal from '../components/FounderNoteModal';
import CalorieComparison from '../components/CalorieComparison';

const HomePage = () => {
  const heroRef = useRef(null);
  const { scrollY } = useScroll();
  const [ingredientText, setIngredientText] = useState('');
  const [showStrike, setShowStrike] = useState(false);
  const [logoClicks, setLogoClicks] = useState(0);
  const [showFounderNote, setShowFounderNote] = useState(false);

  const handleLogoClick = (e) => {
    e.preventDefault();
    const newCount = logoClicks + 1;
    setLogoClicks(newCount);
    if (newCount >= 5) {
      setShowFounderNote(true);
      setLogoClicks(0);
    }
    // Reset counter after 2 seconds of inactivity
    setTimeout(() => setLogoClicks(0), 2000);
  };

  const fullText = "Refined wheat flour (maida), Sugar, Edible vegetable oil (Palm oil), Invert syrup, Liquid glucose, Milk solids, Cocoa solids, Emulsifiers (322, 471, 476), Raising agents (500ii, 503ii), Salt, Flour treatment agent (223), Antioxidant (320), Flavoring substances (natural and nature-identical flavoring substances), Acidity regulator (330), Thickener (415)...";

  useEffect(() => {
    let index = 0;
    const interval = setInterval(() => {
      if (index < fullText.length) {
        setIngredientText(fullText.slice(0, index + 1));
        index++;
      } else {
        clearInterval(interval);
        setTimeout(() => setShowStrike(true), 500);
      }
    }, 20);

    return () => clearInterval(interval);
  }, []);

  const flavors = [
    {
      flavor: 'cookies-cream',
      name: 'Cookies & Cream',
      description: 'The classic you love, made Way Bigger',
      claims: ['10g Protein', '3g Fibre', 'No Palm Oil', 'No Refined Sugar'],
    },
    {
      flavor: 'chocolate',
      name: 'Chocolate',
      description: 'Rich chocolate heaven without the junk',
      claims: ['10g Protein', '3g Fibre', 'No Palm Oil', 'No Refined Sugar'],
    },
    {
      flavor: 'strawberry',
      name: 'Strawberry',
      description: 'Sweet strawberry magic, zero guilt',
      claims: ['10g Protein', '3g Fibre', 'No Palm Oil', 'No Refined Sugar'],
    },
    {
      flavor: 'tiramisu',
      name: 'Tiramisu',
      description: 'Italian indulgence, reimagined',
      claims: ['10g Protein', '3g Fibre', 'No Palm Oil', 'No Refined Sugar'],
    },
  ];

  return (
    <div className="bg-[#0A0A0A] text-white overflow-x-hidden">
      <FounderNoteModal isOpen={showFounderNote} onClose={() => setShowFounderNote(false)} />
      
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-sm border-b border-[#2A2A2A] px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <button
            onClick={handleLogoClick}
            data-testid="nav-logo-link"
            data-cursor={logoClicks > 0 ? `${5 - logoClicks} MORE` : ''}
            className="font-['Bebas_Neue'] text-2xl text-white bg-transparent border-0 p-0 cursor-pointer"
            style={{ letterSpacing: logoClicks > 0 ? `${logoClicks * 0.5}px` : 'normal' }}
          >
            WAY BIG
          </button>
          <div className="flex gap-6">
            <Link to="/story" className="text-white/60 hover:text-[#FF3D00] font-['Outfit'] uppercase text-sm tracking-wider" data-testid="nav-story-link">
              Story
            </Link>
            <Link to="/shop" className="text-white/60 hover:text-[#FF3D00] font-['Outfit'] uppercase text-sm tracking-wider" data-testid="nav-shop-link">
              Shop
            </Link>
          </div>
        </div>
      </nav>

      <section data-testid="hero-section" ref={heroRef} className="min-h-screen relative flex items-center justify-center px-6 pt-32 pb-20 overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              className="text-center lg:text-left"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
            >
              <div className="inline-block bg-[#FF3D00] text-black px-3 py-1 font-['Outfit'] font-bold uppercase text-xs tracking-[0.2em] mb-6">
                Pre-Launch · Dropping Soon
              </div>
              <h1 data-testid="hero-heading" className="font-['Bebas_Neue'] text-5xl sm:text-6xl md:text-6xl uppercase tracking-tighter leading-none mb-6">
                Your childhood biscuit.<br />
                <span className="text-[#FF3D00]">Way Bigger.</span>
              </h1>
              
              <p className="font-['Outfit'] text-base sm:text-lg text-white/80 mb-8 tracking-wide">
                10g Protein · 3g Fibre · Under 200 kcal · No Palm Oil · No Refined Sugar
              </p>

              <div className="flex flex-col gap-4 items-center lg:items-start">
                <WaitlistForm variant="hero" source="hero" />
                <Link
                  to="/story"
                  data-testid="our-story-cta"
                  data-cursor="READ"
                  className="font-['Outfit'] text-sm uppercase tracking-[0.2em] text-white/60 hover:text-[#FF3D00] underline underline-offset-4 decoration-1"
                >
                  Read our story →
                </Link>
              </div>
            </motion.div>

            <motion.div
              className="relative"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, delay: 0.5 }}
              style={{ y: useTransform(scrollY, [0, 500], [0, -80]) }}
            >
              <img
                src="/waybig-packs.jpg"
                alt="Way Big Cream Biscuits - Cookies & Cream, Chocolate, Strawberry"
                className="w-full h-auto object-contain drop-shadow-[0_25px_60px_rgba(255,61,0,0.25)]"
                data-testid="hero-product-image"
              />

              <FloatingSticker className="top-[5%] -left-2 sm:-left-6" rotate={-12} delay={1.0} color="#FF3D00">
                Built in India
              </FloatingSticker>
              <FloatingSticker className="top-[10%] -right-2 sm:-right-4" rotate={10} delay={1.3} color="#FFB300">
                10g Protein
              </FloatingSticker>
              <FloatingSticker className="bottom-[28%] -left-4 sm:-left-10" rotate={-6} delay={1.5} color="#0A0A0A" textColor="#FFB300">
                {'<'}200 Kcal
              </FloatingSticker>
              <FloatingSticker className="bottom-[15%] right-2 sm:right-6" rotate={8} delay={1.7} color="#FFFFFF">
                3g Fibre
              </FloatingSticker>
              <FloatingSticker className="bottom-[2%] left-[20%] sm:left-[30%]" rotate={-3} delay={1.9} color="#FF3D00">
                No Palm Oil
              </FloatingSticker>
            </motion.div>
          </div>
        </div>
      </section>

      <section data-testid="problem-section" className="py-24 px-6 bg-[#FAFAFA]">
        <div className="max-w-4xl mx-auto">
          <div className="typing-text text-sm sm:text-base mb-8">
            {ingredientText}
          </div>
          
          {showStrike && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="text-center mt-12"
            >
              <h2 className="font-['Bebas_Neue'] text-4xl sm:text-5xl text-[#FF3D00] uppercase">
                We read this. Then we rewrote it.
              </h2>
            </motion.div>
          )}
        </div>
      </section>

      <Marquee
        data-testid="claims-marquee"
        speed={80}
        gradient={false}
        className="bg-[#FF3D00] border-y-2 border-black py-6"
      >
        <span className="font-['Bebas_Neue'] text-4xl sm:text-6xl text-black mx-8">10G PROTEIN</span>
        <span className="font-['Bebas_Neue'] text-4xl sm:text-6xl text-black mx-8">·</span>
        <span className="font-['Bebas_Neue'] text-4xl sm:text-6xl text-black mx-8">3G FIBRE</span>
        <span className="font-['Bebas_Neue'] text-4xl sm:text-6xl text-black mx-8">·</span>
        <span className="font-['Bebas_Neue'] text-4xl sm:text-6xl text-black mx-8">HALF THE CALORIES</span>
        <span className="font-['Bebas_Neue'] text-4xl sm:text-6xl text-black mx-8">·</span>
        <span className="font-['Bebas_Neue'] text-4xl sm:text-6xl text-black mx-8">NO PALM OIL</span>
        <span className="font-['Bebas_Neue'] text-4xl sm:text-6xl text-black mx-8">·</span>
        <span className="font-['Bebas_Neue'] text-4xl sm:text-6xl text-black mx-8">NO REFINED SUGAR</span>
        <span className="font-['Bebas_Neue'] text-4xl sm:text-6xl text-black mx-8">·</span>
      </Marquee>

      <section data-testid="flavors-section" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.h2
            className="font-['Bebas_Neue'] text-4xl sm:text-5xl text-center text-white uppercase mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Four Flavours. Zero Compromises.
          </motion.h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {flavors.map((flavor) => (
              <FlavorCard key={flavor.flavor} {...flavor} />
            ))}
          </div>
        </div>
      </section>

      <VoteSection />

      <CalorieComparison />

      <section data-testid="claims-section" className="py-32 px-6 space-y-32">
        <motion.div
          className="min-h-[60vh] flex items-center justify-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="font-['Bebas_Neue'] text-5xl sm:text-6xl text-center text-white uppercase max-w-4xl">
            <span className="text-[#FF3D00]">10 grams</span> of protein.
          </h2>
        </motion.div>

        <motion.div
          className="min-h-[60vh] flex items-center justify-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="font-['Bebas_Neue'] text-5xl sm:text-6xl text-center text-white uppercase max-w-4xl">
            <span className="text-[#FF3D00]">3 grams</span> of fibre. In a biscuit.
          </h2>
        </motion.div>

        <motion.div
          className="min-h-[60vh] flex items-center justify-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="font-['Bebas_Neue'] text-5xl sm:text-6xl text-center text-white uppercase max-w-4xl">
            <span className="text-[#FF3D00]">Half the calories.</span> Same biscuit.
          </h2>
        </motion.div>

        <motion.div
          className="min-h-[60vh] flex items-center justify-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="font-['Bebas_Neue'] text-5xl sm:text-6xl text-center text-white uppercase max-w-4xl">
            <span className="text-[#FF3D00]">Zero palm oil.</span> Including the cream.
          </h2>
        </motion.div>

        <motion.div
          className="min-h-[60vh] flex items-center justify-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="font-['Bebas_Neue'] text-5xl sm:text-6xl text-center text-white uppercase max-w-4xl">
            <span className="text-[#FF3D00]">Zero refined sugar.</span> All the taste.
          </h2>
        </motion.div>
      </section>

      <section data-testid="story-teaser-section" className="py-24 px-6 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-['Bebas_Neue'] text-5xl sm:text-6xl text-white uppercase mb-6">
              Somebody had to do it.
            </h2>
            <p className="font-['Manrope'] text-lg text-white/70 mb-8 leading-relaxed">
              India's most loved snack. The worst ingredients. We fixed it.
            </p>
            <Link
              to="/story"
              data-testid="read-full-story-link"
              className="inline-flex items-center font-['Outfit'] text-[#FF3D00] hover:text-[#FFB300] text-lg uppercase tracking-wider"
            >
              Read the full story →
            </Link>
          </motion.div>

          <motion.div
            className="flex justify-center"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <img
              src="/waybig-packs.jpg"
              alt="Way Big Cream Biscuits"
              className="w-full h-auto object-contain drop-shadow-[0_25px_60px_rgba(255,61,0,0.2)]"
              data-testid="story-teaser-image"
            />
          </motion.div>
        </div>
      </section>

      <section data-testid="community-section" className="py-24 px-6 bg-[#0A0A0A] border-t border-[#2A2A2A]">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block bg-[#FFB300] text-black px-3 py-1 font-['Outfit'] font-bold uppercase text-xs tracking-[0.2em] mb-6">
              Building in Public
            </div>
            <h2 className="font-['Bebas_Neue'] text-4xl sm:text-5xl text-white uppercase mb-6">
              Follow the whole thing.
            </h2>
            <p className="font-['Manrope'] text-lg text-white/70 mb-10 max-w-2xl mx-auto">
              We're documenting every step — recipe iterations, packaging design, factory visits, the chaos. Come along.
            </p>
            <a
              href="https://instagram.com/go.waybig"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-handle-cta"
              className="inline-flex items-center gap-3 bg-transparent border-2 border-[#FF3D00] hover:bg-[#FF3D00] text-[#FF3D00] hover:text-black font-['Bebas_Neue'] text-3xl tracking-wider py-4 px-10 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
              </svg>
              @go.waybig
            </a>
          </motion.div>
        </div>
      </section>

      <FaqSection />

      <section id="waitlist" data-testid="waitlist-section" className="py-24 px-6 bg-[#FF3D00]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2
            className="font-['Bebas_Neue'] text-5xl sm:text-6xl text-black uppercase mb-4"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Be First.
          </motion.h2>
          <motion.p
            className="font-['Manrope'] text-lg text-black/80 mb-12 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            Founding batch is limited. Join the waitlist and get early access, a founding member discount, and our eternal gratitude.
          </motion.p>
          
          <WaitlistForm counterColor="#000000" textColor="#000000" />
        </div>
      </section>

      <footer data-testid="footer" className="py-16 px-6 bg-black border-t border-[#2A2A2A]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="font-['Bebas_Neue'] text-3xl text-white">WAY BIG</div>
            
            <div className="flex gap-6">
              <a
                href="https://instagram.com/go.waybig"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="instagram-link"
                className="text-white/60 hover:text-[#FF3D00] font-['Outfit'] uppercase text-sm tracking-wider"
              >
                @go.waybig
              </a>
              <Link
                to="/shop"
                data-testid="shop-footer-link"
                className="text-white/60 hover:text-[#FF3D00] font-['Outfit'] uppercase text-sm tracking-wider"
              >
                Shop
              </Link>
            </div>
          </div>
          
          <div className="mt-8 text-center text-white/50 font-['Manrope'] text-sm">
            Made in India. For people who read labels.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;